// Comprehensive Knowledge Base for LaundryMate Chatbot
// Supports English and Tagalog queries

export const knowledgeBase = [
  // ==================== PRICING ====================
  {
    id: 'pricing',
    keywords: ['price', 'pricing', 'cost', 'magkano', 'presyo', 'bayad', 'subscription', 'monthly', 'fee', 'plan', 'afford', 'budget', 'mahal', 'mura', 'rate'],
    question: 'Magkano ang LaundryMate?',
    answer: `**LaundryMate Pricing:**

**₱***/month** - All features included

✅ Order Management & Tracking
✅ Customer Database & CRM
✅ Inventory Management
✅ Staff & Payroll Tracking
✅ Expense Tracking
✅ Business Analytics & Reports
✅ Loyalty Program
✅ Mobile App Access
✅ Priority Support

📱 **SMS Credits (Optional):**
₱2,000 = 1,000 SMS credits + FREE sender name

`,
    followUp: ['setup', 'sms-credits', 'features']
  },
  {
    id: 'sms-credits',
    keywords: ['sms', 'text', 'message', 'credits', 'load', 'sender', 'sender name', 'messaging', 'notification', 'remind', 'reminder'],
    question: 'Paano ang SMS credits?',
    answer: `**SMS Credits System:**

📱 **₱2,000 package:**
- 1,000 SMS credits
- FREE custom sender name (e.g., "JuansLaundry")
- Never expires

**How it works:**
- Automated SMS reminders for pickup
- Order status notifications
- Marketing campaigns
- Loyalty updates

**Per SMS:** ₱2 lang pag naubos, top-up ulit

*SMS credits are optional - app works without them!*`,
    followUp: ['pricing', 'features']
  },
  {
    id: 'free-trial',
    keywords: ['free', 'trial', 'try', 'subukan', 'test', 'demo', 'libre', 'free trial', 'no credit card', 'walang bayad'],
    question: 'May free trial ba?',
    answer: `**Wala kaming free trial sa ngayon.**

**Current plan:**
- ₱***/month
- All features included
- Cancel anytime
- 30-day money-back guarantee

**How to get started:**
1. Message us on Messenger
2. We'll create your shop account
3. Start using LaundryMate

👉 Message us: **m.me/946243658562372**`,
    followUp: ['pricing', 'setup']
  },

  // ==================== FEATURES ====================
  {
    id: 'features',
    keywords: ['feature', 'features', 'ano', 'kasama', 'included', 'offer', 'service', 'pwede', 'kaya', 'function', 'capability', 'ano-ano'],
    question: 'Ano-ano ang features ng LaundryMate?',
    answer: `**LaundryMate Features:**

📋 **Order System**
- Simple flow: Created → Processing → Done
- Auto SMS when ready for pickup
- Digital receipts & claim stubs

👥 **Customer Management**
- Customer database with history
- Contact info & preferences
- Automated follow-ups

📦 **Inventory Control**
- Track supplies & stock levels
- Low stock alerts
- Reorder reminders

👨‍💼 **Staff Management**
- Attendance tracking
- Payroll computation
- Performance monitoring

💰 **Financial Tools**
- Expense tracking
- Sales reports
- Profit analytics

📊 **Business Analytics**
- Revenue reports
- Customer insights
- Peak hours analysis

🎁 **Loyalty Program**
- Points system
- Digital loyalty cards
- Customer rewards

📱 **SMS & Campaigns**
- Automated reminders
- Marketing campaigns
- Bulk messaging`,
    followUp: ['order-management', 'inventory', 'loyalty']
  },
  {
    id: 'order-management',
    keywords: ['order', 'orders', 'tracking', 'track', 'status', 'pending', 'ready', 'claim', 'pickup', 'labada', 'order management', 'process'],
    question: 'Paano ang Order System?',
    answer: `**Simple Order System:**

📋 **Easy 3-Step Flow:**
1. **Order Created** - Customer drops off, create order
2. **Processing** - Laundry is being done
3. **Done & Ready** - Auto SMS sent to customer!

**No hassle for staff!**
- Hindi na kailangan i-click every stage
- Simple lang - Processing → Done
- Automatic text pag ready na

**Features:**
✅ Quick order creation
✅ Customer info saved
✅ Auto SMS when ready
✅ Digital receipts
✅ Claim stub printing
✅ Partial payment tracking
✅ Order history

**Dashboard shows:**
- Today's orders
- Ready for pickup
- Revenue tracking`,
    followUp: ['sms-credits', 'receipt-printing']
  },
  {
    id: 'inventory',
    keywords: ['inventory', 'supplies', 'stock', 'detergent', 'fabcon', 'supplies', 'gamit', 'kulang', 'restock', 'low stock'],
    question: 'Paano ang Inventory Management?',
    answer: `**Inventory Management:**

📦 **Track Everything:**
- Detergent
- Fabric conditioner
- Plastic bags
- Hangers
- Any supplies you use

**Features:**
✅ Stock level monitoring
✅ Low stock alerts
✅ Usage tracking per order
✅ Reorder reminders
✅ Supplier management
✅ Cost tracking

**Benefits:**
- Never run out of supplies
- Track expenses accurately
- Know your real costs per order`,
    followUp: ['features', 'expenses']
  },
  {
    id: 'loyalty',
    keywords: ['loyalty', 'points', 'reward', 'rewards', 'promo', 'discount', 'libre', 'freebie', 'punch card', 'stamp', 'loyal'],
    question: 'Paano ang Loyalty Program?',
    answer: `**Digital Loyalty Program:**

🎁 **How it works:**
- Customers earn points per order
- Points = rewards/discounts
- Digital loyalty card (no physical card needed!)

**Features:**
✅ Customizable points system
✅ QR code loyalty cards
✅ Automatic point tracking
✅ Reward redemption
✅ Customer engagement

**Example Setup:**
- ₱100 spent = 1 point
- 10 points = Free wash
- Birthday bonus points

*Customers can check points via their unique link!*`,
    followUp: ['features', 'customers']
  },
  {
    id: 'customers',
    keywords: ['customer', 'customers', 'client', 'database', 'contact', 'info', 'history', 'suki', 'regular', 'crm'],
    question: 'Paano ang Customer Management?',
    answer: `**Customer Database & CRM:**

👥 **Store Customer Info:**
- Name & contact number
- Address (for delivery)
- Order history
- Total spent
- Preferences

**Features:**
✅ Search customers instantly
✅ View complete order history
✅ Track loyal customers
✅ Segment by spending
✅ Automated follow-ups
✅ Birthday tracking

**Benefits:**
- Know your best customers
- Personalized service
- Automated marketing
- Increase retention`,
    followUp: ['loyalty', 'sms-credits']
  },
  {
    id: 'staff',
    keywords: ['staff', 'employee', 'empleyado', 'worker', 'payroll', 'salary', 'sweldo', 'attendance', 'time', 'schedule'],
    question: 'Paano ang Staff Management?',
    answer: `**Staff & Payroll Management:**

👨‍💼 **Track Your Team:**
- Employee profiles
- Attendance tracking
- Work schedules
- Performance metrics

💰 **Payroll Features:**
- Daily/weekly/monthly computation
- Overtime tracking
- Deductions management
- Payslip generation
- Cash advance tracking

**Benefits:**
- Accurate salary computation
- Track attendance easily
- Monitor productivity
- Reduce payroll errors`,
    followUp: ['features', 'expenses']
  },
  {
    id: 'expenses',
    keywords: ['expense', 'expenses', 'gastos', 'cost', 'budget', 'bills', 'utilities', 'rent', 'electric', 'water'],
    question: 'Paano ang Expense Tracking?',
    answer: `**Expense Tracking:**

💸 **Track All Costs:**
- Rent
- Utilities (electric, water)
- Supplies
- Staff salaries
- Maintenance
- Other expenses

**Features:**
✅ Categorized expenses
✅ Monthly summaries
✅ Expense reports
✅ Profit calculation
✅ Budget monitoring

**Benefits:**
- Know your true profit
- Identify cost savings
- Better financial decisions
- Tax preparation ready`,
    followUp: ['features', 'analytics']
  },
  {
    id: 'analytics',
    keywords: ['analytics', 'report', 'reports', 'data', 'sales', 'revenue', 'income', 'profit', 'dashboard', 'insights', 'stats', 'statistics'],
    question: 'Paano ang Business Analytics?',
    answer: `**Business Analytics & Reports:**

📊 **Dashboard Overview:**
- Today's revenue
- Orders count
- Pending orders
- Active customers

📈 **Reports Include:**
- Daily/weekly/monthly sales
- Revenue trends
- Top services
- Peak hours/days
- Customer growth
- Service breakdown

**Insights:**
- Best performing services
- Busiest times
- Customer behavior
- Growth trends

*Make data-driven decisions!*`,
    followUp: ['features', 'dashboard']
  },
  {
    id: 'receipt-printing',
    keywords: ['print', 'printer', 'receipt', 'resibo', 'claim stub', 'bluetooth', 'thermal', 'rawbt'],
    question: 'Paano mag-print ng receipt?',
    answer: `**Receipt & Claim Stub Printing:**

🖨️ **Supported:**
- Bluetooth thermal printers
- Uses RawBT app (free)

**Print Options:**
- Full receipt (with payment)
- Claim stub (for pickup)
- Auto-print on order

**Setup:**
1. Install RawBT app (Android)
2. Connect Bluetooth printer
3. Enable in LaundryMate settings

**Receipt includes:**
- Order details
- Services & prices
- Payment status
- Pickup date
- Shop info

*Works with most 58mm/80mm thermal printers!*`,
    followUp: ['setup', 'features']
  },
  {
    id: 'campaigns',
    keywords: ['campaign', 'marketing', 'promo', 'promotion', 'broadcast', 'bulk', 'send', 'blast', 'advertise'],
    question: 'Paano ang SMS Campaigns?',
    answer: `**SMS & Email Campaigns:**

📣 **Campaign Types:**
- Promotional offers
- Holiday greetings
- New service announcements
- Re-engagement campaigns

**Features:**
✅ Bulk SMS sending
✅ Customer segmentation
✅ Schedule campaigns
✅ Template messages
✅ Campaign analytics

**Example Campaigns:**
- "20% OFF this weekend!"
- "Miss you! Come back for a free service"
- "Happy Birthday! Free fold today"

*Requires SMS credits for text campaigns*`,
    followUp: ['sms-credits', 'customers']
  },

  // ==================== SETUP & GETTING STARTED ====================
  {
    id: 'setup',
    keywords: ['setup', 'start', 'begin', 'register', 'sign up', 'create', 'account', 'paano', 'magsimula', 'gumamit', 'install', 'apply', 'avail'],
    question: 'Paano mag-start sa LaundryMate?',
    answer: `**Getting Started is Easy!**

**Step 1: Contact Us**
- Message us on Messenger
- Tell us your shop name & details
- We'll create your account!

**Step 2: We'll Set Up Your Shop**
- Add your services & prices
- Configure your settings
- Quick orientation call (optional)

**Step 3: Start Using!**
- Login to app.laundrymate.shop
- Add your customers
- Create your first order

**Optional Setup:**
- Connect Bluetooth printer
- Buy SMS credits
- Configure loyalty program

👉 Message us now: **m.me/946243658562372**

*We'll have you running in under 1 hour!*`,
    followUp: ['pricing', 'features']
  },
  {
    id: 'mobile-app',
    keywords: ['app', 'mobile', 'phone', 'android', 'iphone', 'ios', 'download', 'install', 'pwa', 'playstore', 'appstore'],
    question: 'May mobile app ba?',
    answer: `**Yes! LaundryMate is a PWA (Progressive Web App)**

📱 **Works on:**
- Android phones/tablets
- iPhones/iPads
- Desktop computers
- Any device with a browser!

**How to Install (after we set up your account):**
1. Login to app.laundrymate.shop
2. Click "Add to Home Screen"
3. Done! Works like a native app

**Benefits of PWA:**
✅ No app store needed
✅ Always updated
✅ Works offline
✅ Fast & lightweight
✅ Push notifications

*Same experience as native apps!*`,
    followUp: ['setup', 'features']
  },

  // ==================== SUPPORT & HELP ====================
  {
    id: 'support',
    keywords: ['support', 'help', 'tulong', 'assist', 'contact', 'problem', 'issue', 'error', 'bug', 'hindi gumagana', 'ayaw'],
    question: 'Paano mag-contact ng support?',
    answer: `**Customer Support:**

💬 **Contact Us:**
- Messenger: m.me/946243658562372
- Available 24/7

**Support Includes:**
✅ Setup assistance
✅ Technical help
✅ Feature questions
✅ Billing inquiries
✅ Training

**Response Time:**
- Chat: Usually within minutes
- Complex issues: Within 24 hours

*We're here to help you succeed!*`,
    followUp: ['setup', 'pricing']
  },
  {
    id: 'training',
    keywords: ['training', 'tutorial', 'learn', 'guide', 'turuan', 'aral', 'video', 'demo', 'how to'],
    question: 'May training ba?',
    answer: `**Training & Resources:**

📚 **Available:**
- Video tutorials
- Step-by-step guides
- In-app tooltips
- Live chat support

**Free Training:**
- Basic setup walkthrough
- Feature demonstrations
- Best practices tips

**For New Users:**
- 1-on-1 onboarding call (upon request)
- Screen sharing assistance
- Personalized setup help

*Message us on Messenger for training!*`,
    followUp: ['support', 'setup']
  },

  // ==================== TECHNICAL ====================
  {
    id: 'offline',
    keywords: ['offline', 'internet', 'wifi', 'connection', 'no signal', 'walang net', 'data'],
    question: 'Gumagana ba offline?',
    answer: `**Offline Capability:**

📴 **Works Offline:**
- View existing orders
- Basic app navigation
- Cached data access

📶 **Needs Internet For:**
- Creating new orders
- Syncing data
- Sending SMS
- Real-time updates

**Auto-Sync:**
- When back online, data syncs automatically
- No data loss

*Best with stable internet, but basic features work offline!*`,
    followUp: ['mobile-app', 'features']
  },
  {
    id: 'security',
    keywords: ['security', 'secure', 'safe', 'data', 'privacy', 'hack', 'protect', 'backup', 'ligtas'],
    question: 'Secure ba ang data ko?',
    answer: `**Data Security:**

🔒 **We Protect Your Data:**
- Bank-level SSL encryption
- Secure cloud storage (Supabase)
- Regular automated backups
- No data sharing with third parties

**Your Data is:**
✅ Encrypted in transit
✅ Encrypted at rest
✅ Backed up daily
✅ Only accessible by you

**Privacy:**
- We never sell your data
- GDPR-compliant practices
- You own your data

*Your business data is safe with us!*`,
    followUp: ['features', 'support']
  },
  {
    id: 'multiple-shops',
    keywords: ['multiple', 'branch', 'branches', 'shops', 'locations', 'marami', 'iba', 'another', 'expand'],
    question: 'Pwede ba multiple shops?',
    answer: `**Multi-Shop Support:**

🏪 **Yes! Manage Multiple Locations:**
- Separate dashboard per shop
- Switch between shops easily
- Consolidated reporting

**Features:**
✅ Individual shop settings
✅ Separate staff per location
✅ Combined analytics
✅ Single account login

**Pricing:**
- Each shop = separate subscription
- Volume discounts available for 3+ shops

*Contact us for Enterprise pricing!*`,
    followUp: ['pricing', 'support']
  },

  // ==================== COMPARISONS ====================
  {
    id: 'vs-notebook',
    keywords: ['notebook', 'manual', 'papel', 'pen', 'logbook', 'excel', 'spreadsheet', 'dating', 'old', 'traditional'],
    question: 'Bakit LaundryMate vs notebook?',
    answer: `**LaundryMate vs Manual/Notebook:**

📓 **Notebook Problems:**
❌ Lost records
❌ Hard to search
❌ No customer history
❌ Manual calculations
❌ Forgotten follow-ups
❌ No analytics

📱 **LaundryMate Benefits:**
✅ All records searchable
✅ Auto calculations
✅ Customer history saved
✅ Automated reminders
✅ Real-time analytics
✅ Never lose data

**Real Results:**
- Less admin work
- No forgotten pickups (auto SMS!)
- Professional customer service

*Modern problems need modern solutions!*`,
    followUp: ['features', 'pricing']
  },
  {
    id: 'vs-others',
    keywords: ['other', 'compare', 'competitor', 'alternative', 'iba', 'difference', 'better', 'best'],
    question: 'Bakit LaundryMate vs other apps?',
    answer: `**Why Choose LaundryMate:**

🏆 **Built for PH Laundry Shops:**
- Tagalog support
- Peso currency
- PH SMS integration
- Local payment methods

**Advantages:**
✅ All-in-one solution
✅ Affordable pricing
✅ No hidden fees
✅ 24/7 local support
✅ Regular updates
✅ Mobile-first design

**Complete Package:**
- Orders + Inventory + Staff + Expenses + Analytics
- Other apps need multiple subscriptions!

*Made by Pinoys, for Pinoy businesses!*`,
    followUp: ['pricing', 'features']
  },

  // ==================== COMMON QUESTIONS ====================
  {
    keywords: ['cancel', 'stop', 'unsubscribe', 'quit', 'end', 'itigil'],
    question: 'Pwede ba mag-cancel anytime?',
    answer: `**Yes! Cancel Anytime**

✅ No long-term contracts
✅ No cancellation fees
✅ No questions asked

**How to Cancel:**
- Message us on Messenger
- We'll process within 24 hours
- Access until end of billing period

**Your Data:**
- Export your data anytime
- We keep backups for 30 days
- Then permanently deleted

*But we hope you'll stay! 😊*`,
    followUp: ['pricing', 'support']
  },
  {
    keywords: ['refund', 'money back', 'guarantee', 'balik', 'pera'],
    question: 'May money-back guarantee ba?',
    answer: `**30-Day Money-Back Guarantee!**

💯 **Our Promise:**
- Not satisfied within 30 days?
- Full refund, no questions asked!

**How it Works:**
1. Try LaundryMate for 30 days
2. Not happy? Contact us
3. Get full refund

**Why We Offer This:**
- We're confident you'll love it
- Zero risk for you
- We want happy customers only

*Your satisfaction is guaranteed!*`,
    followUp: ['pricing', 'support']
  },
  {
    keywords: ['payment', 'pay', 'bayaran', 'gcash', 'maya', 'bank', 'card', 'credit'],
    question: 'Paano mag-bayad?',
    answer: `**Payment Methods:**

💳 **We Accept:**
- GCash
- Maya/PayMaya
- Bank Transfer
- Credit/Debit Cards

**Billing:**
- Monthly subscription
- Billed every 30 days
- Receipt provided

**SMS Credits:**
- One-time purchase
- Top-up anytime
- Never expires

*Easy and flexible payment!*`,
    followUp: ['pricing', 'sms-credits']
  },

  // ==================== GREETINGS & GENERAL ====================
  {
    keywords: ['hi', 'hello', 'hey', 'kumusta', 'musta', 'good morning', 'good afternoon', 'good evening', 'magandang', 'yo', 'uy'],
    question: 'Greeting',
    answer: `**Hello! 👋 Welcome to LaundryMate!**

I'm here to help you learn about our laundry shop management system.

**Quick Links:**
• Pricing & Plans
• Features Overview
• How to Get Started
• Support & Setup

What would you like to know? 😊`,
    followUp: ['pricing', 'features', 'setup']
  },
  {
    keywords: ['thanks', 'thank', 'salamat', 'ty', 'thank you', 'maraming salamat'],
    question: 'Thanks',
    answer: `**You're welcome! 🙏**

Glad I could help!

**Need more help?**
- Ask me anything else
- Or chat with our team: m.me/946243658562372

Good luck with your laundry business! 🧺✨`,
    followUp: ['support', 'pricing']
  },
  {
    keywords: ['bye', 'goodbye', 'paalam', 'sige', 'okay', 'ok', 'sige na'],
    question: 'Goodbye',
    answer: `**Goodbye! 👋**

Thanks for chatting with me!

**Remember:**
- ₱***/month all-in plan
- We'll set up your account for you
- Just message us on Messenger!

👉 **m.me/946243658562372**

Take care and good luck! 🧺💪`,
    followUp: ['pricing']
  }
];

// Quick reply suggestions for common flows
export const quickReplies = [
  { id: 'pricing', label: '💰 Pricing' },
  { id: 'features', label: '✨ Features' },
  { id: 'sms-credits', label: '📱 SMS Credits' },
  { id: 'setup', label: '🚀 How to Start' },
  { id: 'support', label: '💬 Contact Support' }
];

// Fallback response when no match found
export const fallbackResponse = {
  answer: `**Hindi ko masagot yan, pero kaya ko 'to! 🤔**

Try asking about:
• **Pricing** - Magkano ang LaundryMate?
• **Features** - Ano-ano ang features?
• **How to Start** - Paano mag-avail?
• **SMS Credits** - Paano ang SMS?

Or chat with a human: **m.me/946243658562372**`,
  followUp: ['pricing', 'features', 'setup', 'support']
};

// Function to find best matching response
export function findBestMatch(query) {
  const normalizedQuery = query.toLowerCase().trim();

  // Check for exact keyword matches first
  let bestMatch = null;
  let highestScore = 0;

  for (const item of knowledgeBase) {
    let score = 0;

    for (const keyword of item.keywords) {
      if (normalizedQuery.includes(keyword.toLowerCase())) {
        // Longer keyword matches = higher score
        score += keyword.length;
      }
    }

    if (score > highestScore) {
      highestScore = score;
      bestMatch = item;
    }
  }

  // Return match if score is good enough, otherwise fallback
  if (highestScore >= 3) {
    return bestMatch;
  }

  return null;
}

// Get response by ID (for follow-up questions)
export function getResponseById(id) {
  return knowledgeBase.find(item => item.id === id);
}
